import React, { useCallback, useContext, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { EmptyArray } from 'lib/array/arrayUtils'
import checkPersonalisedSaving from 'luxPlus/lib/checkPersonalisedSaving'
import useLuxPlusLandingActions from 'luxPlus/hooks/useLuxPlusLandingActions'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import ModalContext from 'contexts/ModalContext'
import GeoContext from 'contexts/geoContext'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import Caption from 'LuxKit/Typography/Caption'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import clsx from 'clsx'
import LuxPlusLogo from '../Logos/LuxPlusLogo'
import LuxPlusStatCards from './LuxPlusStatCards'
import LuxPlusPromoBannerCarousel from './LuxPlusPromoBannerCarousel'
import LuxPlusLandingAboutSection from 'luxPlus/components/LandingPage/LuxPlusLandingAboutSection'
import LuxPlusLandingBenefitsSection from './LuxPlusLandingBenefitsSection'
import LuxPlusLandingPartnerOffersTabSection from './LuxPlusLandingPartnerOffersTabSection'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getRegionSaveAmount } from 'luxPlus/lib/getRegionSaveAmountCopy'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import zIndex from 'styles/tools/z-index'
import { useLuxPlusPromoBanners } from 'luxPlus/hooks/useLuxPlusPromos'
import usePartnerPromoOffers from 'luxPlus/hooks/usePartnerPromoOffers'
import useLuxPlusLandingTab from 'luxPlus/hooks/useLuxPlusLandingTab'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchTotalPotentialSavings } from 'actions/LuxPlusActions'
import LuxPlusPotentialSavingsModal from '../modals/LuxPlusPotentialSavingsModal'
import LuxPlusLandingNavigationProvider from 'luxPlus/contexts/LuxPlusLandingNavigationProvider'
import { type LuxPlusLandingNonMemberTab } from 'luxPlus/contexts/LuxPlusLandingNavigationContext'
import focusStyle from 'styles/utils/focusStyle'
import variables from 'styles/vendor/bootstrap/variables'
import { scrollToId } from 'lib/window/scrollUtils'

const CTAContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${rem(12)};
  margin-bottom: ${rem(16)};
`

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(32)};
  align-items: center;

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr 1fr;
  }
`

const HeroLayoutContainer = styled(LayoutContainer)`
  padding-right: 0;
  padding-left: 0;

  ${mediaQueryUp.desktop} {
    padding-right: ${rem(variables.gutterWidth / 2)};
    padding-left: ${rem(variables.gutterWidth / 2)};
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(16)};

  ${mediaQueryUp.desktop} {
    align-items: flex-start;
  }
`

const StatCardsWrapper = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    flex-direction: column;
    gap: ${rem(24)};
  }
`

const MobileStatCards = styled.div`
  display: block;

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const Footnote = styled(Caption)`
  opacity: 0.8;
  max-width: ${rem(300)};
  margin: 0 auto;
  text-align: center;

  ${mediaQueryUp.desktop} {
    max-width: none;
  }
`

const HeroSheet = styled(Sheet)`
  background: linear-gradient(90deg, ${props => props.theme.palette.product.luxPlus.background} 0%, #4C82DF 100%);
`

const NavSection = styled(Pane)`
  position: sticky;
  z-index: ${zIndex.stickyHeaderBanner};
  top: 0;
  padding: ${rem(32)} 0 ${rem(20)} 0;
`

const TabContentSpacer = styled.div`
  height: ${rem(32)};
`

const ViewToggle = styled.div`
  display: flex;
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${rem(20)};
  padding: ${rem(4)};
  gap: ${rem(4)};
`

const ToggleButton = styled.button`
  padding: ${rem(8)} ${rem(20)};
  border: none;
  border-radius: ${rem(16)};
  font-size: ${rem(14)};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  background: transparent;
  color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};

  &:hover {
    color: ${props => props.theme.palette.neutral.surface.lightForegroundAlt};
  }

  &:focus-visible {
    ${focusStyle}
  }

  &.active {
    background: ${props => props.theme.palette.neutral.surface.normalBackground};
    color: ${props => props.theme.palette.neutral.surface.normalContrast};
    box-shadow: ${props => props.theme.shadow.bottom.small};

    &:hover {
      color: ${props => props.theme.palette.neutral.surface.normalContrast};
    }
  }
`

const STICKY_NAV_SCROLL_OFFSET = -100
const NON_MEMBER_TABS: ReadonlyArray<LuxPlusLandingNonMemberTab> = ['about', 'benefits', 'partner-offers']

function LuxPlusLoggedOffLandingPageContent() {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)
  const saveAmount = getRegionSaveAmount(currentRegionCode)

  const dispatch = useAppDispatch()
  const [stickyRef, notSticky] = useInView()
  const { activeTab, navigateToTab } = useLuxPlusLandingTab(NON_MEMBER_TABS, 'about')

  const firstName = useAppSelector(state => state.auth.account.givenName)
  const hasSubscriptionOffers = useAppSelector(state => !!state.luxPlus.products.subscription.offers)
  const totalPotentialSavings = useAppSelector(state => state.luxPlus.totalPotentialSavings.data)
  const personalSaving = totalPotentialSavings?.totalAmount
  const savingsItems: Array<App.LuxPlusPotentialSavingsItem> = totalPotentialSavings?.items ?? EmptyArray
  const { showPersonalised } = checkPersonalisedSaving(firstName, personalSaving)

  useEffect(() => {
    dispatch(fetchTotalPotentialSavings())
  }, [dispatch])

  const handleTabAbout = useCallback(() => navigateToTab('about'), [navigateToTab])
  const handleTabBenefits = useCallback(() => navigateToTab('benefits'), [navigateToTab])
  const handleTabPartnerOffers = useCallback(() => {
    navigateToTab('partner-offers')
    requestAnimationFrame(() => {
      scrollToId('partner_offers_list', STICKY_NAV_SCROLL_OFFSET)
    })
  }, [navigateToTab])

  const { handleBecomeMember, handleBecomeMemberNoJoinFee, handleStartFreePreview, isSpoofed } = useLuxPlusLandingActions()
  const hasJoinFeeFreeOffer = useAppSelector(
    state => !!state.luxPlus.products.subscription.offers?.some(o => o.joinFeeWaived),
  )

  const handleSignIn = useCallback(() => {
    showModal<AccountAccessModalResult>(<AccountAccessModal />)
  }, [showModal])

  const handlePastBookingsClick = useCallback(() => {
    if (savingsItems.length > 0 && personalSaving !== undefined) {
      showModal(<LuxPlusPotentialSavingsModal items={savingsItems} totalSaving={personalSaving} />)
    }
  }, [showModal, savingsItems, personalSaving])

  const promoBanners = useLuxPlusPromoBanners()
  const partnerOffers = usePartnerPromoOffers()
  const showPartnerOffersTab = partnerOffers.length >= 3

  const heroHeading = intl.formatMessage({
    id: '-kBO3mwLj9gYeaHonUCi',
    defaultMessage: 'Your VIP Travel Club',
    description: 'Hero heading on the LuxPlus logged-off landing page content',
  })

  const heroSubheading = intl.formatMessage({
    id: 'KBxI7TNIw5P-njf71N0U',
    defaultMessage: 'Unpack a world of member-only benefits.',
    description: 'Hero subheading on LuxPlus logged-off landing page',
  })

  const becomeMemberLabel = intl.formatMessage({
    id: '50G7bLfEyI9VPBij6k5I',
    defaultMessage: 'Become a member',
    description: 'CTA button to start LuxPlus membership purchase on logged-off landing page',
  })

  const becomeMemberNoJoinFeeLabel = intl.formatMessage({
    id: 'kZptru9JkmD5d-3fAtrH',
    defaultMessage: 'Become a member (no joining fee)',
    description: 'Spoof-only CTA button to start LuxPlus membership purchase without joining fee',
  })

  const startFreePreviewLabel = intl.formatMessage({
    id: 'KkElsn1BXHWJJpgNb0iA',
    defaultMessage: 'Start free preview',
    description: 'CTA button to start a free LuxPlus preview on logged-off landing page',
  })

  const signInLabel = intl.formatMessage({
    id: 'uj9GkqTLdw44VTmsd5iy',
    defaultMessage: 'Sign in',
    description: 'Text link for existing members to sign in on LuxPlus logged-off landing page',
  })

  let footnoteText: string
  if (showPersonalised) {
    footnoteText = intl.formatMessage({
      id: 'eCw68lCh8gzdInKwPmH-',
      defaultMessage: '*Based on your actual bookings and {programName} pricing at the time of purchase.',
      description: 'Footnote disclaimer about savings calculation on LuxPlus landing page',
    }, { programName: LUXURY_PLUS.PROGRAM_NAME })
  } else {
    footnoteText = intl.formatMessage({
      id: 'oqoDbcJUNzDwsjzSzQMq',
      defaultMessage: '*Based on savings by top 50% of active {programName} members over 12 months',
      description: 'Footnote disclaimer about savings calculation on LuxPlus landing page',
    }, { programName: LUXURY_PLUS.PROGRAM_NAME })
  }

  const aboutTabLabel = intl.formatMessage({
    id: '44kT2MggRLwSErniPcPV',
    defaultMessage: 'About',
    description: 'Tab label for the About section on LuxPlus landing page',
  })

  const benefitsTabLabel = intl.formatMessage({
    id: '5SpkviQdtWDk6OcsoqkP',
    defaultMessage: 'Benefits',
    description: 'Tab label for the Benefits section on LuxPlus landing page',
  })

  const partnerOffersTabLabel = intl.formatMessage({
    id: 'R-9iZhfbVJvWCOce6x8A',
    defaultMessage: 'Partner Offers',
    description: 'Tab label for the Partner Offers section on LuxPlus landing page',
  })

  return (
    <>
      <HeroSheet padding="40 10" tabletPadding="40 20" paletteType="inverse">
        <HeroLayoutContainer>
          <HeroGrid>
            <LeftColumn>
              <LuxPlusLogo height={20} />
              <HeadingTextBlock variant="heading1" align="center" tabletAlign="start">{heroHeading}</HeadingTextBlock>
              <BodyText variant="medium" align="center" colour="normal-contrast">
                {heroSubheading}
              </BodyText>
              <MobileStatCards>
                <LuxPlusStatCards saveAmount={saveAmount} firstName={firstName} personalSaving={personalSaving} onPastBookingsClick={handlePastBookingsClick} />
              </MobileStatCards>
              <CTAContainer>
                <TextButton kind="primary" disabled={!hasSubscriptionOffers} onClick={handleBecomeMember}>
                  {becomeMemberLabel}
                </TextButton>
                {isSpoofed && hasJoinFeeFreeOffer && <TextButton kind="primary" onClick={handleBecomeMemberNoJoinFee}>
                  {becomeMemberNoJoinFeeLabel}
                </TextButton>}
                <TextButton kind="secondary" onClick={handleStartFreePreview}>
                  {startFreePreviewLabel}
                </TextButton>
              </CTAContainer>
              <BodyText variant="medium" colour="normal-contrast">
                <FormattedMessage
                  id="R2Yg1UFqRkOjkj05Z93W"
                  defaultMessage="Already a {programName} member? {signInLink}"
                  description="Prompt for existing LuxPlus members to sign in on logged-off landing page"
                  values={{
                    programName: LUXURY_PLUS.PROGRAM_NAME,
                    signInLink: <TextLink onClick={handleSignIn}>{signInLabel}</TextLink>,
                  }}
                />
              </BodyText>
              <CSSBreakpoint max="tablet">
                <Footnote variant="medium" colour="normal-contrast">
                  {footnoteText}
                </Footnote>
              </CSSBreakpoint>
            </LeftColumn>
            <StatCardsWrapper>
              <LuxPlusStatCards saveAmount={saveAmount} firstName={firstName} personalSaving={personalSaving} onPastBookingsClick={handlePastBookingsClick} />
              <CSSBreakpoint min="mobile">
                <Footnote variant="medium" colour="normal-contrast">
                  {footnoteText}
                </Footnote>
              </CSSBreakpoint>
            </StatCardsWrapper>
          </HeroGrid>
        </HeroLayoutContainer>
      </HeroSheet>

      {promoBanners.length > 0 && <LuxPlusPromoBannerCarousel items={promoBanners} />}

      <NavSection shadow={!notSticky ? 'bottom-small' : undefined}>
        <LayoutContainer variant="medium">
          <Group direction="horizontal" horizontalAlign="center">
            <ViewToggle>
              <ToggleButton
                className={clsx({ active: activeTab === 'about' })}
                onClick={handleTabAbout}
                type="button"
              >
                {aboutTabLabel}
              </ToggleButton>
              <ToggleButton
                className={clsx({ active: activeTab === 'benefits' })}
                onClick={handleTabBenefits}
                type="button"
              >
                {benefitsTabLabel}
              </ToggleButton>
              {showPartnerOffersTab && <ToggleButton
                className={clsx({ active: activeTab === 'partner-offers' })}
                onClick={handleTabPartnerOffers}
                type="button"
              >
                {partnerOffersTabLabel}
              </ToggleButton>}
            </ViewToggle>
          </Group>
        </LayoutContainer>
      </NavSection>
      <div ref={stickyRef} />

      <TabContentSpacer />
      <LuxPlusLandingNavigationProvider navigateToPartnerOffers={handleTabPartnerOffers} scrollOffset={STICKY_NAV_SCROLL_OFFSET}>
        {activeTab === 'about' && <LuxPlusLandingAboutSection />}
        {activeTab === 'benefits' && <LuxPlusLandingBenefitsSection />}
        {activeTab === 'partner-offers' && showPartnerOffersTab && <LuxPlusLandingPartnerOffersTabSection />}
      </LuxPlusLandingNavigationProvider>
    </>
  )
}

export default LuxPlusLoggedOffLandingPageContent
